import React, { useMemo, useState } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';

const PROMPT = '[nixdojo-demo]$ ';

const INITIAL_FILES = {
    'README.txt': 'Run the curated lesson commands from the left panel.',
};

const CURATED_COMMANDS = [
    'pwd',
    'ls -la',
    'whoami',
    'echo "hello nixdojo" > note.txt',
    'cat note.txt',
    'rm note.txt',
    'clear',
    'help',
];

function formatListing(files) {
    const fileRows = Object.keys(files).map((fileName) => `-rw-r--r-- 1 demo demo ${String(files[fileName].length).padStart(4, ' ')} Jul 20 12:00 ${fileName}`);
    return ['total 12', 'drwxr-xr-x 2 demo demo 4096 Jul 20 12:00 .', 'drwxr-xr-x 3 demo demo 4096 Jul 20 12:00 ..', ...fileRows].join('\n');
}

function helpText() {
    return [
        'Welcome to the NixDojo mock demo terminal.',
        '',
        'This browser-only demo accepts only these curated commands:',
        ...CURATED_COMMANDS.map((command, index) => `  ${index + 1}. ${command}`),
        '',
        'No shell, container, pod, or backend terminal is started for this public demo.',
    ].join('\n');
}

function runCuratedCommand(command, files) {
    switch (command) {
        case 'pwd':
            return { output: '/home/demo', files };
        case 'ls -la':
            return { output: formatListing(files), files };
        case 'whoami':
            return { output: 'demo', files };
        case 'echo "hello nixdojo" > note.txt':
            return { output: '', files: { ...files, 'note.txt': 'hello nixdojo\n' } };
        case 'cat note.txt':
            return {
                output: files['note.txt'] || 'cat: note.txt: No such file or directory\nHint: run echo "hello nixdojo" > note.txt first.',
                files,
            };
        case 'rm note.txt': {
            const nextFiles = { ...files };
            delete nextFiles['note.txt'];
            return { output: '', files: nextFiles };
        }
        case 'help':
            return { output: helpText(), files };
        case 'clear':
            return { output: helpText(), files, clear: true };
        case '':
            return { output: '', files };
        default:
            return {
                output: `Blocked: "${command}" is not in the curated demo task list. Type "help" to see allowed commands.`,
                files,
            };
    }
}

const MockDemoTerminal = () => {
    const welcome = useMemo(helpText, []);
    const [files, setFiles] = useState(INITIAL_FILES);
    const [history, setHistory] = useState([{ command: null, output: welcome }]);
    const [currentCommand, setCurrentCommand] = useState('');

    const submitCommand = (event) => {
        event.preventDefault();
        const normalizedCommand = currentCommand.trim();
        const result = runCuratedCommand(normalizedCommand, files);
        setFiles(result.files);
        setHistory((previousHistory) => {
            const nextEntry = { command: normalizedCommand, output: result.output };
            return result.clear ? [{ command: null, output: result.output }] : [...previousHistory, nextEntry];
        });
        setCurrentCommand('');
    };

    return (
        <Box
            aria-label="Mock demo terminal"
            sx={{
                height: '80vh',
                width: '100%',
                border: '1px solid',
                borderColor: '#1e293b',
                borderRadius: 1,
                boxShadow: '0 18px 32px rgba(15, 23, 42, 0.24)',
                overflow: 'hidden',
                background: 'linear-gradient(180deg, #111827 0%, #0f172a 100%)',
                color: '#e2e8f0',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                p: 2,
            }}
        >
            <Stack component="form" onSubmit={submitCommand} spacing={1} sx={{ height: '100%' }}>
                <Box sx={{ flex: 1, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 14 }}>
                    {history.map((entry, index) => (
                        <Box key={`${entry.command || 'welcome'}-${index}`} sx={{ mb: 1 }}>
                            {entry.command !== null && <Typography component="div" sx={{ fontFamily: 'inherit', color: '#a5b4fc' }}>{PROMPT}{entry.command}</Typography>}
                            {entry.output && <Typography component="pre" sx={{ m: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}>{entry.output}</Typography>}
                        </Box>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography component="label" htmlFor="mock-demo-command" sx={{ color: '#a5b4fc', fontFamily: 'inherit' }}>{PROMPT}</Typography>
                    <TextField
                        id="mock-demo-command"
                        value={currentCommand}
                        onChange={(event) => setCurrentCommand(event.target.value)}
                        variant="standard"
                        fullWidth
                        autoComplete="off"
                        inputProps={{ 'aria-label': 'Curated demo command' }}
                        sx={{
                            input: { color: '#e2e8f0', fontFamily: 'inherit' },
                            '& .MuiInput-underline:before': { borderBottomColor: '#334155' },
                            '& .MuiInput-underline:hover:before': { borderBottomColor: '#64748b' },
                            '& .MuiInput-underline:after': { borderBottomColor: '#818cf8' },
                        }}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

export default MockDemoTerminal;
